import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { onboardingApi } from 'entities/onboarding/api/onboardingApi';
import { onboardingKeys } from 'entities/onboarding/api/queryKeys';
import type { StepStatus } from 'entities/onboarding/types';

type Options = { enabled?: boolean };

export function useChecklist({ enabled = true }: Options = {}) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: onboardingKeys.checklist(),
    queryFn: () => onboardingApi.getChecklist(),
    enabled,
    staleTime: 0,
    refetchOnMount: 'always',
    // A 404 before the wizard is completed is expected (callers also gate `enabled` on
    // wizard_complete), and the client's 4xx errors carry no status to branch on — so retry only
    // transient server (5xx) errors, which do.
    retry: (failureCount, error) =>
      failureCount < 2 &&
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      typeof error.status === 'number' &&
      error.status >= 500,
  });

  // Awaited so updateStep.mutateAsync resolves only once the checklist has refetched — the caller
  // (narrative mode) relies on the current step having advanced before it records the settled one.
  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: onboardingKeys.checklist() }),
      queryClient.invalidateQueries({ queryKey: onboardingKeys.state() }),
    ]);

  const updateStep = useMutation({
    mutationFn: ({ stepId, status }: { stepId: string; status: Extract<StepStatus, 'complete' | 'skipped'> }) =>
      onboardingApi.updateStep(stepId, status),
    onSuccess: invalidate,
  });

  const syncChecklist = useMutation({
    mutationFn: () => onboardingApi.syncChecklist(),
    onSuccess: invalidate,
  });

  return {
    query,
    updateStep: updateStep.mutateAsync,
    syncChecklist: syncChecklist.mutateAsync,
  };
}
